import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import { useClipboard } from "@galaxy-io/dls/hooks/useClipboard";
import CopyInput from "@galaxy-io/dls/inputs/CopyInput";
import DateInput, { DateInputVariant } from "@galaxy-io/dls/inputs/DateInput";
import Field from "@galaxy-io/dls/inputs/Field";
import { InputVariant } from "@galaxy-io/dls/inputs/Input";
import Flex, { AlignItems, FlexDirection } from "@galaxy-io/dls/layout/Flex";
import { MenuItem, MenuItemVariant } from "@galaxy-io/dls/menu/Menu";
import Span from "@galaxy-io/dls/text/Span";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import Widget, { WidgetSize } from "@galaxy-io/dls/widget/Widget";
import type { IssuedMcpToken, McpToken } from "@gtm/contracts/mcp-tokens";
import { CopyIcon, KeyIcon, PlusIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { Stack } from "@/components/workspace/people-styles";
import { RecordTable } from "@/components/workspace/record-table";
import { SelectField } from "@/components/workspace/select-field";
import { Status } from "@/components/workspace/status";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";
import { SettingsSection } from "./settings-header";

function expirationDate(date: Date | null) {
  if (!date) return null;
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay;
}
export function McpTokens({ userId, workspaceName }: { userId: string; workspaceName: string }) {
  const cache = useQueryClient();
  const { toast } = useToast();
  const queryKey = ["mcp-tokens", userId];
  const tokens = useQuery({ queryKey, queryFn: () => unwrap(apiClient().GET("/mcp-tokens")) });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [expires, setExpires] = useState<Date | null>(null);
  const [access, setAccess] = useState("read");
  const [issued, setIssued] = useState<IssuedMcpToken | null>(null);
  const [revoking, setRevoking] = useState<McpToken | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const endpoint = `${window.location.origin}/api/mcp`;
  const validExpiry = !!expires && expires.getTime() > Date.now();

  function close() {
    if (busy) return;
    setIssued(null);
    setOpen(false);
    setError("");
  }
  const clipboard = useClipboard();
  async function copy(value: string, label: string) {
    if (await clipboard.copy(value)) {
      toast({
        variant: ToastVariant.SUCCESS,
        header: `${label} copied`,
        description: "Ready to paste into your MCP client.",
        ...toastDefaults(ToastVariant.SUCCESS),
      });
    } else {
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not copy",
        description: "Select the value and copy it manually.",
        ...toastDefaults(ToastVariant.ERROR),
      });
    }
  }
  async function create() {
    if (busy || !name.trim() || !expires || !validExpiry) return;
    setBusy(true);
    setError("");
    try {
      const result = await unwrap(
        apiClient().POST("/mcp-tokens", {
          body: {
            name: name.trim(),
            expiresAt: expires.toISOString(),
            allowActions: access === "actions",
          },
        }),
      );
      // The secret is held only in this dialog, never in the query cache or storage.
      setIssued(result);
      void cache.invalidateQueries({ queryKey });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function revoke() {
    if (!revoking || busy) return;
    setBusy(true);
    setError("");
    try {
      await unwrap(
        apiClient().POST("/mcp-tokens/{id}/revoke", { params: { path: { id: revoking.id } } }),
      );
      cache.setQueryData<McpToken[]>(queryKey, (current) =>
        current?.map((token) =>
          token.id === revoking.id ? { ...token, revoked_at: new Date().toISOString() } : token,
        ),
      );
      setRevoking(null);
      void cache.invalidateQueries({ queryKey });
    } finally {
      setBusy(false);
    }
  }
  return (
    <SettingsSection id="mcp-tokens" title="MCP tokens">
      <Widget
        header="Developer access"
        subheader="Connect your AI tools to this workspace with MCP."
        icon={KeyIcon}
        size={WidgetSize.LARGE}
        isFlush
        gap={0}
        actions={
          <Button
            label="Create token"
            icon={PlusIcon}
            onClick={() => {
              setName("");
              const defaultExpiry = new Date();
              defaultExpiry.setDate(defaultExpiry.getDate() + 30);
              setExpires(expirationDate(defaultExpiry));
              setAccess("read");
              setIssued(null);
              setError("");
              setOpen(true);
            }}
            size={ButtonSize.SMALL}
          />
        }
      >
        <Flex alignItems={AlignItems.CENTER} gap={16} padding={16}>
          <Flex direction={FlexDirection.COLUMN} gap={4} grow={1} minWidth={0}>
            <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM}>
              Server URL
            </Text>
            <Text
              family={FontFamily.MONO}
              size={TextSize.BODY_SM}
              variant={TextVariant.SECONDARY}
              lineClamp={1}
              isSelectable
            >
              {endpoint}
            </Text>
          </Flex>
          <Button
            label="Copy server URL"
            icon={CopyIcon}
            variant={ButtonVariant.SECONDARY}
            onClick={() => void copy(endpoint, "Server URL")}
            size={ButtonSize.SMALL}
          />
        </Flex>

        <RecordTable
          rowActions={(row) => row.actions}
          fillHeight={false}
          label="MCP tokens"
          emptyState={
            <EmptyState
              header="No access tokens yet"
              description="Create a token to connect an MCP client. Each token has its own permissions and expiry."
            />
          }
          isLoading={tokens.isPending}
          error={tokens.error || undefined}
          onRetry={() => void tokens.refetch()}
          rowKeys={(tokens.data || []).map((token) => token.id)}
          rows={(tokens.data || []).map((token) => {
            const expired = Date.parse(token.expires_at) <= Date.now();
            const status = token.revoked_at ? "revoked" : expired ? "expired" : "active";
            return {
              name: <Span weight={TextWeight.MEDIUM}>{token.name}</Span>,
              access: token.allowActions ? "Read and actions" : "Read only",
              status: <Status value={status} />,
              expires: new Date(token.expires_at).toLocaleDateString(),
              actions:
                status === "active" ? (
                  <MenuItem
                    label="Revoke"
                    variant={MenuItemVariant.ERROR}
                    onSelect={() => {
                      setError("");
                      setRevoking(token);
                    }}
                  />
                ) : null,
            };
          })}
          columns={[
            { key: "name", label: "Name", width: "auto" },
            { key: "access", label: "Access", width: 160 },
            { key: "status", label: "Status", width: 120 },
            { key: "expires", label: "Expires", width: 150 },
          ]}
        />
      </Widget>
      <SurfaceDialog
        open={open}
        title={issued ? "Token created" : "Create MCP token"}
        busy={busy}
        onClose={close}
        footer={
          issued ? (
            <Button label="Done" variant={ButtonVariant.SECONDARY} onClick={close} />
          ) : (
            <>
              <Button
                label="Cancel"
                variant={ButtonVariant.SECONDARY}
                isDisabled={busy}
                onClick={close}
              />
              <Button
                label="Create token"
                isLoading={busy}
                isDisabled={busy || !name.trim() || !validExpiry}
                onClick={() => void create()}
              />
            </>
          )
        }
      >
        <Stack>
          {issued ? (
            <>
              <Text>
                Copy this token now. You won’t be able to see it again after closing this dialog.
              </Text>
              <CopyInput
                label="Authorization header value"
                value={`Bearer ${issued.token}`}
                variant={InputVariant.BASE}
                fillWidth
                family={FontFamily.MONO}
              />
              <CopyInput
                label="Server URL"
                value={endpoint}
                variant={InputVariant.BASE}
                fillWidth
              />
              <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                In Claude, use the Server URL above and select No sign-in. Add a required request
                header named Authorization and paste the Authorization header value above. Bearer is
                already included. Leave OAuth client settings empty.
              </Text>
            </>
          ) : (
            <>
              <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                Connect to {workspaceName} with your workspace permissions.
              </Text>
              <Field label="Name">
                <Input
                  aria-label="Token name"
                  placeholder="e.g. My MCP client"
                  value={name}
                  onChange={(event) => setName(event)}
                  disabled={busy}
                  autoComplete="off"
                  autoFocus
                />
              </Field>
              <DateInput
                max={new Date()}
                label="Expiration date"
                value={expires}
                min={new Date(new Date().setHours(0, 0, 0, 0))}
                onChange={(date) => setExpires(expirationDate(date))}
                variant={DateInputVariant.BASE}
                fillWidth
                isDisabled={busy}
                isRequired
              />
              <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                Expires at the end of the selected day in your local time.
              </Text>
              <SelectField
                label="Access"
                value={access}
                disabled={busy}
                options={[
                  { value: "read", label: "Read only" },
                  { value: "actions", label: "Read and actions" },
                ]}
                onChange={setAccess}
              />
              <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                {access === "actions"
                  ? "Allows enrichment, sync, edits and sending to saved destinations, within your current workspace permissions."
                  : "Allows queries, signal layering and viewing run history. Cannot make changes."}
              </Text>
            </>
          )}
          {error && (
            <Alert variant={AlertVariant.ERROR}>
              {
                <>
                  <Alert variant={AlertVariant.ERROR}>{error}</Alert>
                </>
              }
            </Alert>
          )}
        </Stack>
      </SurfaceDialog>
      <ConfirmAction
        isOpen={!!revoking}
        header={`Revoke ${revoking?.name ?? "token"}?`}
        description={
          <>Clients using this token will lose access. Work already in progress may finish.</>
        }
        label="Revoke token"
        isDestructive
        onOpenChange={(next) => {
          if (!next)
            (() => {
              if (!busy) {
                setRevoking(null);
                setError("");
              }
            })();
        }}
        onConfirm={async () => revoke()}
      />
    </SettingsSection>
  );
}
